import { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import { hitApi, commonToast } from "../../../components/commonAction";
import BillingTabs from "./BillingTabs";
import "../../../styles/admin-drawer.css";

const EMPTY_FORM = {
    productId: "",
    code: "",
    name: "",
    description: "",
    billingType: "one_time",
    billingInterval: "",
    price: 0,
    currency: "USD",
    isActive: true,
};

export default function Plans({ pageTitle }) {
    const [products, setProducts] = useState([]);
    const [filterProductId, setFilterProductId] = useState("");
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM, productId: filterProductId || "" });
        setDrawerOpen(true);
    };
    const closeDrawer = () => setDrawerOpen(false);

    // Reset form a tick after the drawer slides out for a clean animation.
    useEffect(() => {
        if (drawerOpen) return;
        const t = setTimeout(() => {
            setEditingId(null);
            setForm(prev => ({ ...EMPTY_FORM, productId: filterProductId || "" }));
        }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerOpen]);

    useEffect(() => {
        if (!drawerOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drawerOpen]);

    const loadProducts = () => {
        setLoading(true);
        hitApi({ url: "admin/billing/products", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setProducts(resp.data?.products || []);
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(loadProducts, []);

    // public catalog endpoint already returns products with embedded plans;
    // reuse that to render the plan list rather than building yet another endpoint
    const [allPlans, setAllPlans] = useState([]);
    const loadPlans = () => {
        hitApi({ url: "billing/products", method: "GET", data: {}, alert: false }, (resp) => {
            if (resp?.status) {
                const flat = [];
                (resp.data?.products || []).forEach(p => {
                    (p.plans || []).forEach(pl => flat.push({ ...pl, productCode: p.code, productName: p.name }));
                });
                setAllPlans(flat);
            }
        });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(loadPlans, []);

    const filteredPlans = useMemo(() => {
        if (!filterProductId) return allPlans;
        return allPlans.filter(p => String(p.productId) === String(filterProductId));
    }, [allPlans, filterProductId]);

    const startEdit = (p) => {
        setEditingId(p._id);
        setForm({
            productId: p.productId,
            code: p.code,
            name: p.name,
            description: p.description || "",
            billingType: p.billingType,
            billingInterval: p.billingInterval || "",
            price: p.price,
            currency: p.currency || "USD",
            isActive: !!p.isActive,
        });
        setDrawerOpen(true);
    };
    const cancelEdit = () => { closeDrawer(); };

    const submit = (e) => {
        e.preventDefault();
        if (!form.productId || !form.code.trim() || !form.name.trim()) {
            commonToast("error", "Product, code, and name are required.");
            return;
        }
        setSubmitting(true);

        const billingInterval = form.billingType === "recurring" ? (form.billingInterval || "monthly") : null;
        const body = {
            ...form,
            billingInterval,
            price: Number(form.price) || 0,
        };

        const url = editingId
            ? `admin/billing/plans/${editingId}`
            : `admin/billing/products/${form.productId}/plans`;
        const method = editingId ? "PATCH" : "POST";

        hitApi({ url, method, data: body }, (resp) => {
            setSubmitting(false);
            if (resp?.status) {
                commonToast("success", resp.message);
                closeDrawer();
                loadPlans();
            }
        });
    };

    const remove = (p) => {
        if (!window.confirm(`Soft-delete plan "${p.name}"?`)) return;
        hitApi({ url: `admin/billing/plans/${p._id}`, method: "DELETE", data: {} }, (resp) => {
            if (resp?.status) { commonToast("success", resp.message); loadPlans(); }
        });
    };

    return (
        <div className="pxl_rightBar_assets">
            <Helmet><title>{pageTitle}</title></Helmet>
            <div className="pxl-rb-header-wrap"><div className="pxl-headingTop"><h2 className="pxl-rb-header-heading">Billing</h2></div></div>
            <BillingTabs />

            <div className="row g-3">
                <div className="col-12">
                    <div className="pxl-panel">
                        <div className="pxl-panel-header">
                            <div className="d-flex align-items-center gap-2">
                                <span>Plans ({filteredPlans.length})</span>
                                <select
                                    style={{ width: "auto", padding: "6px 10px", borderRadius: 6, border: "1px solid #e3e7f0", fontSize: 13 }}
                                    value={filterProductId}
                                    onChange={e => setFilterProductId(e.target.value)}
                                >
                                    <option value="">All products</option>
                                    {products.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                                </select>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn btn-sm btn-light" onClick={() => { loadProducts(); loadPlans(); }}>Refresh</button>
                                <button className="pxl-drawer-new-btn" onClick={openCreate}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                                    </svg>
                                    New Plan
                                </button>
                            </div>
                        </div>
                        <div className="pxl-panel-body pxl-panel-body--flush">
                            {!loading && filteredPlans.length === 0 ? (
                                <div className="text-muted" style={{ padding: 24, textAlign: "center" }}>No plans yet.</div>
                            ) : (
                                <div className="pxl_table table-responsive">
                                    <table className="pxl_table">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>Plan</th>
                                                <th>Type</th>
                                                <th>Price</th>
                                                <th>Status</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <>
                                                    {[0, 1, 2, 3, 4].map(i => (
                                                        <tr key={i}>
                                                            <td><span className="pxl-sk" style={{ width: '55%', height: 13, display: 'block' }} /></td>
                                                            <td><span className="pxl-sk" style={{ width: '50%', height: 13, display: 'block' }} /></td>
                                                            <td><span className="pxl-sk" style={{ width: '45%', height: 13, display: 'block' }} /></td>
                                                            <td><span className="pxl-sk" style={{ width: '40%', height: 13, display: 'block' }} /></td>
                                                            <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                                            <td>
                                                                <div style={{ display: 'flex', gap: 8 }}>
                                                                    <span className="pxl-sk" style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-block' }} />
                                                                    <span className="pxl-sk" style={{ width: 28, height: 28, borderRadius: 6, display: 'inline-block' }} />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </>
                                            ) : filteredPlans.map(p => (
                                                <tr key={p._id}>
                                                    <td><p>{p.productName}</p></td>
                                                    <td><p><code>{p.code}</code> {p.name}</p></td>
                                                    <td><p>{p.billingType}{p.billingInterval ? `/${p.billingInterval}` : ""}</p></td>
                                                    <td><p>{p.currency} {Number(p.price).toFixed(2)}</p></td>
                                                    <td>
                                                        {p.isActive
                                                            ? <span className="pxl-pill pxl-pill--success">active</span>
                                                            : <span className="pxl-pill pxl-pill--muted">inactive</span>}
                                                    </td>
                                                    <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                                        <button className="btn btn-sm btn-link" onClick={() => startEdit(p)}>Edit</button>
                                                        <button className="btn btn-sm btn-link" style={{ color: "#d04050" }} onClick={() => remove(p)}>Delete</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Right-side drawer: create / edit plan ── */}
            <div
                className={`pxl-drawer-backdrop${drawerOpen ? ' is-open' : ''}`}
                onClick={closeDrawer}
            />
            <aside
                className={`pxl-drawer${drawerOpen ? ' is-open' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={editingId ? 'Edit plan' : 'New plan'}
            >
                <div className="pxl-drawer-header">
                    <div>
                        <h3 className="pxl-drawer-title">{editingId ? 'Edit Plan' : 'New Plan'}</h3>
                        <p className="pxl-drawer-sub">
                            {editingId
                                ? `Code: ${form.code}`
                                : 'Add a new plan under a product.'}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="pxl-drawer-close"
                        onClick={closeDrawer}
                        title="Close"
                        aria-label="Close"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                <form onSubmit={submit} className="pxl-drawer-body" id="planForm">
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Product</label>
                        <select
                            value={form.productId}
                            onChange={e => setForm({ ...form, productId: e.target.value })}
                            disabled={!!editingId}
                        >
                            <option value="">Select product…</option>
                            {products.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Plan code</label>
                        <input
                            value={form.code}
                            onChange={e => setForm({ ...form, code: e.target.value })}
                            disabled={!!editingId}
                            placeholder="basic / pro / premium"
                        />
                    </div>
                    <div className="pxl_input_feilds pxl-input-compact">
                        <label>Name</label>
                        <input
                            value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })}
                        />
                    </div>
                    <div className="row">
                        <div className="col-7">
                            <div className="pxl_input_feilds pxl-input-compact">
                                <label>Billing</label>
                                <select
                                    value={form.billingType}
                                    onChange={e => setForm({ ...form, billingType: e.target.value })}
                                    disabled={!!editingId}
                                >
                                    <option value="one_time">One-time</option>
                                    <option value="recurring">Recurring</option>
                                </select>
                            </div>
                        </div>
                        <div className="col-5">
                            <div className="pxl_input_feilds pxl-input-compact">
                                <label>Interval</label>
                                <select
                                    value={form.billingInterval}
                                    onChange={e => setForm({ ...form, billingInterval: e.target.value })}
                                    disabled={form.billingType === "one_time"}
                                >
                                    <option value="">—</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="yearly">Yearly</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-7">
                            <div className="pxl_input_feilds pxl-input-compact">
                                <label>Price</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    value={form.price}
                                    onChange={e => setForm({ ...form, price: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="col-5">
                            <div className="pxl_input_feilds pxl-input-compact">
                                <label>Currency</label>
                                <input
                                    value={form.currency}
                                    onChange={e => setForm({ ...form, currency: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="form-check mb-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="planActive"
                            checked={form.isActive}
                            onChange={e => setForm({ ...form, isActive: e.target.checked })}
                        />
                        <label className="form-check-label" htmlFor="planActive">Active</label>
                    </div>
                    {form.billingType === "recurring" && editingId && (
                        <small style={{ display: "block", color: "#8a8fa6", fontSize: 12, marginTop: 4 }}>
                            Note: editing price on a live recurring plan creates a new PayPal plan;
                            existing subscriptions keep the old price.
                        </small>
                    )}
                </form>

                <div className="pxl-drawer-footer">
                    <button type="submit" form="planForm" className="pxlCommon-btn" disabled={submitting}>
                        {submitting ? (editingId ? 'Saving…' : 'Creating…') : (editingId ? 'Save' : 'Create')}
                    </button>
                    <button type="button" className="btn btn-light" onClick={cancelEdit}>Cancel</button>
                </div>
            </aside>
        </div>
    );
}
